import React, { useEffect, useMemo, useState } from 'react';
import { Page } from '../../layout/Page';
import { Icon } from '../../design/Icon';
import { accountTimeZone, calendarDay, timeZoneLabel } from '../../design/timeZone';
import { Alert, Drawer, ErrorState, useToast } from '../../components/shared';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { WEEK, formatClock, formatHolidayDate, fullWeek, holidayChip, parseClock, summarizeWeeklyHours } from './adminModel';
import { useAdminData } from './useAdminData';
import { useShellStatus } from '../../layout/ShellStatus';
import { TIME_ZONES } from '../onboarding/onboardingLogic';
import './admin.css';

const SUBTITLE = 'The targets SLA is measured against, and the hours and holidays when it counts.';
const HOURS_MODE = { AccountHours: 'Business hours', CustomHours: 'Custom hours', AfterHoursAndHolidays: 'After hours and holidays' };
const ZONE_NAMES = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Mountain Time (Arizona)',
  'America/Los_Angeles': 'Pacific Time',
};
const DEFAULT_INTERVAL = { start: '08:00:00', end: '17:00:00' };


/** Whole number within bounds, else null. */
const intIn = (text, min, max) => {
  const n = Number(String(text).trim());
  return String(text).trim() !== '' && Number.isFinite(n) && Math.round(n) === n && n >= min && n <= max ? n : null;
};

const numIn = (text, min, max) => {
  const n = Number(String(text).trim());
  return String(text).trim() !== '' && Number.isFinite(n) && n >= min && n <= max ? n : null;
};

/** Seven-day grid of open switches and opening/closing times; reports text it can't read through onValidity. */
function WeekHoursGrid({ week, onChange, onValidity, idPrefix }) {
  const [texts, setTexts] = useState({});
  const days = fullWeek(week);

  useEffect(() => {
    const bad = Object.values(texts).some((t) => t.invalid);
    if (onValidity) onValidity(!bad);
  }, [texts, onValidity]);

  const setDay = (day, patch) => onChange(days.map((d) => (d.day === day ? { ...d, ...patch } : d)));
  const textFor = (day, field, value) => (texts[`${day}-${field}`] ? texts[`${day}-${field}`].text : formatClock(value));

  const edit = (d, field, text) => {
    const parsed = parseClock(text);
    setTexts((t) => ({ ...t, [`${d.day}-${field}`]: { text, invalid: parsed == null } }));
    if (parsed != null) {
      const intervals = d.intervals.length ? [...d.intervals] : [{ ...DEFAULT_INTERVAL }];
      intervals[0] = { ...intervals[0], [field]: parsed };
      setDay(d.day, { intervals });
    }
  };

  return (
    <div className="hours">
      <span className="caption">Day</span>
      <span className="caption">Open</span>
      <span className="caption">Opens</span>
      <span className="caption">Closes</span>
      {days.map((d) => {
        const first = d.intervals[0] || DEFAULT_INTERVAL;
        const startInvalid = texts[`${d.day}-start`]?.invalid;
        const endInvalid = texts[`${d.day}-end`]?.invalid;
        const backwards = d.isOpen && first.end <= first.start;
        return (
          <React.Fragment key={d.day}>
            <span className="small strong">{d.day}</span>
            <label className="switch">
              <input
                type="checkbox"
                aria-label={`Open on ${d.day}`}
                checked={d.isOpen}
                onChange={(e) => setDay(d.day, { isOpen: e.target.checked, intervals: e.target.checked && !d.intervals.length ? [{ ...DEFAULT_INTERVAL }] : d.intervals })}
              />
            </label>
            {d.isOpen ? (
              <>
                <input
                  className={`input${startInvalid || backwards ? ' invalid' : ''}`}
                  id={`${idPrefix}-${d.day}-start`}
                  aria-label={`${d.day} opens`}
                  value={textFor(d.day, 'start', first.start)}
                  onChange={(e) => edit(d, 'start', e.target.value)}
                  title={backwards ? 'Closing time must be after opening time' : undefined}
                />
                <input
                  className={`input${endInvalid || backwards ? ' invalid' : ''}`}
                  id={`${idPrefix}-${d.day}-end`}
                  aria-label={`${d.day} closes`}
                  value={textFor(d.day, 'end', first.end)}
                  onChange={(e) => edit(d, 'end', e.target.value)}
                  title={backwards ? 'Closing time must be after opening time' : undefined}
                />
              </>
            ) : (
              <span className="caption" style={{ gridColumn: 'span 2' }}>
                Closed
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const weekIsValid = (week) => fullWeek(week).every((d) => !d.isOpen || d.intervals.every((i) => i.end > i.start));

export function AdminSlaPage() {
  const toast = useToast();
  const { refresh: refreshShell } = useShellStatus();
  const { data, loading, error, retry } = useAdminData({ sla: adminAPI.getSlaSettings, hours: adminAPI.getBusinessHours, entities: () => adminAPI.getRoutingEntities(90) }, { optional: ['entities'] });
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [targets, setTargets] = useState(null);
  const [hoursValid, setHoursValid] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (!data) return;
    const initial = { sla: data.sla, hours: { ...data.hours, weeklyHours: fullWeek(data.hours.weeklyHours) } };
    setSaved(initial);
    setForm(initial);
    setTargets({
      answerWithinSeconds: String(data.sla.default.answerWithinSeconds),
      answerTargetPercent: String(data.sla.default.answerTargetPercent),
      abandonTargetPercent: String(data.sla.default.abandonTargetPercent),
      ignoreShortCallsUnderSeconds: String(data.sla.ignoreShortCallsUnderSeconds),
    });
  }, [data]);

  const queues = useMemo(
    () => (data?.entities || []).filter((e) => e.routingEntityType === 'CallQueue').sort((a, b) => a.routingEntityName.localeCompare(b.routingEntityName)),
    [data]
  );

  const actionsFor = (onSave, disabled) => (
    <button className="btn btn-primary" type="button" onClick={onSave} disabled={disabled}>
      {busy ? 'Saving…' : 'Save changes'}
    </button>
  );

  if (error) {
    return (
      <Page title="SLA and hours" subtitle={SUBTITLE}>
        <ErrorState title="SLA settings couldn't load" body="Vertolytics couldn't read the SLA targets and business hours. SLA figures keep using the last saved settings." error={error} onRetry={retry} />
      </Page>
    );
  }

  if (loading || !form || !targets) {
    return (
      <Page title="SLA and hours" subtitle={SUBTITLE} actions={actionsFor(() => {}, true)}>
        <div className="grid">
          {['c-6', 'c-6', 'c-7', 'c-5'].map((c, i) => (
            <section className={`card ${c} is-loading`} key={i}>
              <div className="card-body">
                <div style={{ height: 220 }} />
              </div>
            </section>
          ))}
        </div>
      </Page>
    );
  }

  const parsed = {
    answerWithinSeconds: intIn(targets.answerWithinSeconds, 1, 3600),
    answerTargetPercent: numIn(targets.answerTargetPercent, 0, 100),
    abandonTargetPercent: numIn(targets.abandonTargetPercent, 0, 100),
    ignoreShortCallsUnderSeconds: intIn(targets.ignoreShortCallsUnderSeconds, 0, 120),
  };
  const targetsValid = Object.values(parsed).every((v) => v != null);
  const nextSla = targetsValid
    ? {
        ...form.sla,
        default: { ...form.sla.default, answerWithinSeconds: parsed.answerWithinSeconds, answerTargetPercent: parsed.answerTargetPercent, abandonTargetPercent: parsed.abandonTargetPercent },
        ignoreShortCallsUnderSeconds: parsed.ignoreShortCallsUnderSeconds,
      }
    : form.sla;
  const slaDirty = JSON.stringify(nextSla) !== JSON.stringify(saved.sla);
  const hoursDirty = JSON.stringify(form.hours) !== JSON.stringify(saved.hours);
  const valid = targetsValid && hoursValid && weekIsValid(form.hours.weeklyHours);

  const save = async () => {
    if (!slaDirty && !hoursDirty) {
      toast('No changes to save.', 'info');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const sla = slaDirty ? await adminAPI.saveSlaSettings(nextSla) : saved.sla;
      const hoursResult = hoursDirty ? await adminAPI.saveBusinessHours(form.hours) : saved.hours;
      const next = { sla, hours: { ...hoursResult, weeklyHours: fullWeek(hoursResult.weeklyHours) } };
      const zoneChanged = hoursResult.timeZone !== saved.hours.timeZone;
      setSaved(next);
      setForm(next);
      if (zoneChanged) refreshShell?.();
      toast(zoneChanged ? 'Saved. Dates and times now use the new time zone.' : 'Saved. SLA figures recalculate from today forward.');
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setTarget = (key) => (e) => setTargets((t) => ({ ...t, [key]: e.target.value }));
  const queueName = (target) => target.queueName || queues.find((q) => q.routingEntityId.toLowerCase() === target.queueId.toLowerCase())?.routingEntityName || target.queueId;
  const hoursLabel = (target) => (target.hoursMode === 'CustomHours' ? summarizeWeeklyHours(target.weeklyHours) : HOURS_MODE[target.hoursMode] || HOURS_MODE.AccountHours);
  const zone = form.hours.timeZone || accountTimeZone();
  const today = calendarDay(new Date(), zone);
  const holidays = [...(form.hours.holidays || [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const years = [...new Set(holidays.map((h) => String(h.date).slice(0, 4)))];
  const holidayTitle = years.length === 1 ? `Holidays and closures, ${years[0]}` : years.length === 0 ? `Holidays and closures, ${today.slice(0, 4)}` : 'Holidays and closures';
  const setHolidays = (list) => setForm((f) => ({ ...f, hours: { ...f.hours, holidays: list } }));

  const fieldRow = (id, label, key, unit, hint) => {
    const invalid = parsed[key] == null;
    return (
      <div className="field">
        <label className="label" htmlFor={id}>
          {label}
        </label>
        <div className="row">
          <input className={`input${invalid ? ' invalid' : ''}`} id={id} value={targets[key]} style={{ maxWidth: 90 }} inputMode="decimal" onChange={setTarget(key)} aria-invalid={invalid || undefined} />
          <span className="small muted">{unit}</span>
        </div>
        {hint && <span className="hint">{hint}</span>}
      </div>
    );
  };

  return (
    <Page title="SLA and hours" subtitle={SUBTITLE} actions={actionsFor(save, busy || !valid)}>
      {problem && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="error" title="SLA settings couldn't be saved">
            {problem}
          </Alert>
        </div>
      )}
      <div className="grid">
        <section className="card c-6">
          <div className="card-head">
            <div>
              <div className="t">Default targets</div>
              <div className="d">Used by every queue unless overridden below</div>
            </div>
          </div>
          <div className="card-body stack">
            <div className="form-grid">
              {fieldRow('t1', 'Answer within', 'answerWithinSeconds', 'seconds')}
              {fieldRow('t2', 'Target', 'answerTargetPercent', '% of calls')}
              {fieldRow('t3', 'Abandon rate under', 'abandonTargetPercent', '%')}
              {fieldRow('t4', 'Ignore hang-ups shorter than', 'ignoreShortCallsUnderSeconds', 'seconds', 'Callers who hang up this fast are excluded from SLA and abandon rate.')}
            </div>
            <div className="panel small">
              In plain words:{' '}
              <strong>
                {parsed.answerTargetPercent ?? '—'}% of business-hours calls should be answered within {parsed.answerWithinSeconds ?? '—'} seconds
              </strong>
              , and fewer than {parsed.abandonTargetPercent ?? '—'}% should hang up while waiting.
            </div>
          </div>
        </section>
        <section className="card c-6">
          <div className="card-head">
            <div>
              <div className="t">Queue overrides</div>
            </div>
            <div className="acts">
              <button className="btn btn-sm" type="button" onClick={() => setEditing({ isNew: true })}>
                Add override
              </button>
            </div>
          </div>
          <div className="card-body flush">
            <table className="table compact">
              <thead>
                <tr>
                  <th>Queue</th>
                  <th className="n">Answer within</th>
                  <th className="n">Target</th>
                  <th>Hours</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(form.sla.queueTargets || []).map((t, i) => (
                  <tr key={t.queueId}>
                    <td className="cell-title">{queueName(t)}</td>
                    <td className="n">{t.targets.answerWithinSeconds} s</td>
                    <td className="n">{Number(t.targets.answerTargetPercent)}%</td>
                    <td className="small">{hoursLabel(t)}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => setEditing({ index: i, target: t })}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(form.sla.queueTargets || []).length && (
              <p className="caption" style={{ padding: '12px 16px' }}>
                No overrides. Every queue uses the default targets and business hours.
              </p>
            )}
          </div>
        </section>
        <section className="card c-7">
          <div className="card-head">
            <div>
              <div className="t">Business hours</div>
              <div className="d">
                {`${ZONE_NAMES[zone] || timeZoneLabel(zone)} (${zone})`}. Calls outside these hours don't count toward SLA.
              </div>
            </div>
            <div className="acts">
              <label className="sr-only" htmlFor="bh-zone">
                Time zone
              </label>
              <select
                id="bh-zone"
                className="select"
                style={{ width: 'auto' }}
                value={zone}
                onChange={(e) => setForm((f) => ({ ...f, hours: { ...f.hours, timeZone: e.target.value } }))}
              >
                {[...TIME_ZONES, ...(TIME_ZONES.some((z) => z.value === zone) ? [] : [{ value: zone, label: `${timeZoneLabel(zone)} (${zone})` }])].map((z) => (
                  <option key={z.value} value={z.value}>
                    {z.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-body">
            <WeekHoursGrid idPrefix="bh" week={form.hours.weeklyHours} onChange={(week) => setForm((f) => ({ ...f, hours: { ...f.hours, weeklyHours: week } }))} onValidity={setHoursValid} />
            {!weekIsValid(form.hours.weeklyHours) && (
              <div className="field-error mt-3">
                <Icon name="alert" className="ico-sm" />
                Business hours must end after they start.
              </div>
            )}
          </div>
        </section>
        <section className="card c-5">
          <div className="card-head">
            <div>
              <div className="t">{holidayTitle}</div>
              <div className="d">Office closed all day</div>
            </div>
            <div className="acts">
              <button className="btn btn-sm" type="button" onClick={() => setAdding({ date: '', name: '', kind: 'Holiday' })} disabled={adding != null}>
                Add date
              </button>
            </div>
          </div>
          <div className="card-body flush">
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {adding && (
                <li className="row" style={{ padding: '9px 16px', borderTop: '1px solid var(--divider)', gap: 8 }}>
                  <input className="input" type="date" aria-label="Date" value={adding.date} style={{ maxWidth: 160, height: 32 }} onChange={(e) => setAdding((a) => ({ ...a, date: e.target.value }))} />
                  <input className="input grow" aria-label="Name" placeholder="Name, for example Storm closure" value={adding.name} style={{ height: 32 }} onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))} />
                  <select className="select" aria-label="Kind" value={adding.kind} style={{ height: 32, width: 140 }} onChange={(e) => setAdding((a) => ({ ...a, kind: e.target.value }))}>
                    <option value="Holiday">Holiday</option>
                    <option value="AdHocClosure">Ad-hoc closure</option>
                  </select>
                  <button
                    className="btn btn-sm btn-primary"
                    type="button"
                    disabled={!adding.date || !adding.name.trim() || holidays.some((h) => h.date === adding.date)}
                    title={holidays.some((h) => h.date === adding.date) ? 'That date is already listed' : undefined}
                    onClick={() => {
                      setHolidays([...(form.hours.holidays || []), { date: adding.date, name: adding.name.trim(), kind: adding.kind, isClosed: true, intervals: [] }]);
                      setAdding(null);
                    }}
                  >
                    Add
                  </button>
                  <button className="btn btn-sm btn-ghost" type="button" onClick={() => setAdding(null)}>
                    Cancel
                  </button>
                </li>
              )}
              {holidays.map((h) => (
                <li key={h.date} className="row" style={{ padding: '9px 16px', borderTop: '1px solid var(--divider)', gap: 12 }}>
                  <span className="num small strong" style={{ width: 52 }}>
                    {formatHolidayDate(h.date)}
                  </span>
                  <span className="small grow">{h.name}</span>
                  {(() => {
                    const chip = holidayChip(h, today);
                    return chip && <span className={`badge ${chip[1]}`.trim()}>{chip[0]}</span>;
                  })()}
                  <button
                    className="btn btn-ghost btn-sm icon-btn"
                    type="button"
                    aria-label={`Remove ${h.name}`}
                    title={`Remove ${h.name}`}
                    onClick={() => setHolidays((form.hours.holidays || []).filter((x) => x.date !== h.date))}
                  >
                    <Icon name="trash" className="ico-sm" />
                  </button>
                </li>
              ))}
              {!holidays.length && !adding && (
                <li className="caption" style={{ padding: '12px 16px', borderTop: '1px solid var(--divider)' }}>
                  No holidays yet. Add the days the office is closed.
                </li>
              )}
            </ul>
          </div>
        </section>
      </div>
      <OverrideDrawer
        editing={editing}
        queues={queues}
        used={(form.sla.queueTargets || []).map((t) => t.queueId.toLowerCase())}
        defaults={nextSla.default}
        onClose={() => setEditing(null)}
        onSave={(target) => {
          setForm((f) => {
            const list = [...(f.sla.queueTargets || [])];
            if (editing.isNew) list.push(target);
            else list[editing.index] = target;
            return { ...f, sla: { ...f.sla, queueTargets: list } };
          });
          setEditing(null);
        }}
        onRemove={() => {
          setForm((f) => ({ ...f, sla: { ...f.sla, queueTargets: (f.sla.queueTargets || []).filter((_, i) => i !== editing.index) } }));
          setEditing(null);
        }}
      />
    </Page>
  );
}

function OverrideDrawer({ editing, queues, used, defaults, onClose, onSave, onRemove }) {
  const [form, setForm] = useState(null);
  const [gridValid, setGridValid] = useState(true);

  useEffect(() => {
    if (!editing) {
      setForm(null);
      return;
    }
    const t = editing.target;
    setForm({
      queueId: t ? t.queueId : '',
      queueName: t ? t.queueName : '',
      answerWithinSeconds: String(t ? t.targets.answerWithinSeconds : defaults.answerWithinSeconds),
      answerTargetPercent: String(t ? t.targets.answerTargetPercent : defaults.answerTargetPercent),
      abandonTargetPercent: String(t ? t.targets.abandonTargetPercent : defaults.abandonTargetPercent),
      hoursMode: t ? t.hoursMode || 'AccountHours' : 'AccountHours',
      weeklyHours: fullWeek(t && t.weeklyHours ? t.weeklyHours : WEEK.slice(0, 5).map((day) => ({ day, isOpen: true, intervals: [{ start: '08:00:00', end: '17:00:00' }] }))),
      base: t ? t.targets : defaults,
    });
  }, [editing, defaults]);

  if (!editing || !form) return null;
  const options = queues.filter((q) => !used.includes(q.routingEntityId.toLowerCase()) || q.routingEntityId.toLowerCase() === form.queueId.toLowerCase());
  const answer = intIn(form.answerWithinSeconds, 1, 3600);
  const target = numIn(form.answerTargetPercent, 0, 100);
  const abandon = numIn(form.abandonTargetPercent, 0, 100);
  const valid = form.queueId && answer != null && target != null && abandon != null && (form.hoursMode !== 'CustomHours' || (gridValid && weekIsValid(form.weeklyHours) && form.weeklyHours.some((d) => d.isOpen)));
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing.isNew ? 'Add queue override' : `Override for ${form.queueName || 'queue'}`}
      footer={
        <>
          {!editing.isNew && (
            <button className="btn btn-ghost" type="button" onClick={onRemove} style={{ marginRight: 'auto' }}>
              <Icon name="trash" />
              Remove override
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            type="button"
            disabled={!valid}
            onClick={() =>
              onSave({
                queueId: form.queueId,
                queueName: form.queueName || null,
                targets: { ...form.base, answerWithinSeconds: answer, answerTargetPercent: target, abandonTargetPercent: abandon },
                hoursMode: form.hoursMode,
                weeklyHours: form.hoursMode === 'CustomHours' ? form.weeklyHours : null,
              })
            }
          >
            {editing.isNew ? 'Add override' : 'Apply'}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="label" htmlFor="ov-queue">
            Queue
          </label>
          <select
            className="select"
            id="ov-queue"
            value={form.queueId}
            disabled={!editing.isNew}
            onChange={(e) => {
              const q = queues.find((x) => x.routingEntityId === e.target.value);
              setForm((f) => ({ ...f, queueId: e.target.value, queueName: q ? q.routingEntityName : '' }));
            }}
          >
            {editing.isNew && <option value="">Choose a queue</option>}
            {!editing.isNew && !options.some((q) => q.routingEntityId.toLowerCase() === form.queueId.toLowerCase()) && <option value={form.queueId}>{form.queueName || form.queueId}</option>}
            {options.map((q) => (
              <option key={q.routingEntityId} value={q.routingEntityId}>
                {q.routingEntityName}
              </option>
            ))}
          </select>
          {editing.isNew && !queues.length && <span className="hint">Call queues appear here once call records sync.</span>}
        </div>
        <div className="form-grid">
          <div className="field">
            <label className="label" htmlFor="ov-answer">
              Answer within
            </label>
            <div className="row">
              <input className={`input${answer == null ? ' invalid' : ''}`} id="ov-answer" style={{ maxWidth: 90 }} value={form.answerWithinSeconds} onChange={set('answerWithinSeconds')} />
              <span className="small muted">seconds</span>
            </div>
          </div>
          <div className="field">
            <label className="label" htmlFor="ov-target">
              Target
            </label>
            <div className="row">
              <input className={`input${target == null ? ' invalid' : ''}`} id="ov-target" style={{ maxWidth: 90 }} value={form.answerTargetPercent} onChange={set('answerTargetPercent')} />
              <span className="small muted">% of calls</span>
            </div>
          </div>
          <div className="field">
            <label className="label" htmlFor="ov-abandon">
              Abandon rate under
            </label>
            <div className="row">
              <input className={`input${abandon == null ? ' invalid' : ''}`} id="ov-abandon" style={{ maxWidth: 90 }} value={form.abandonTargetPercent} onChange={set('abandonTargetPercent')} />
              <span className="small muted">%</span>
            </div>
          </div>
          <div className="field">
            <label className="label" htmlFor="ov-hours">
              Hours
            </label>
            <select className="select" id="ov-hours" value={form.hoursMode} onChange={set('hoursMode')}>
              {Object.entries(HOURS_MODE).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {form.hoursMode === 'CustomHours' && (
          <WeekHoursGrid idPrefix="ov" week={form.weeklyHours} onChange={(week) => setForm((f) => ({ ...f, weeklyHours: week }))} onValidity={setGridValid} />
        )}
        <p className="caption">
          {form.hoursMode === 'AfterHoursAndHolidays'
            ? 'SLA for this queue counts only calls outside business hours and on holidays.'
            : form.hoursMode === 'CustomHours'
              ? 'SLA for this queue counts calls in these hours, in the account time zone. Holidays still apply.'
              : 'SLA for this queue counts calls in the business hours below.'}
        </p>
      </div>
    </Drawer>
  );
}
